/**
 * Warm the boards the landing page doesn't already read.
 *
 * Signing in lands on `/overview`, which reads the Cloud, Licences, Websites
 * and M365 feeds to build its tiles — so by the time you click into any of
 * those boards, their entry query is already cached and the board paints
 * instantly. Network and Security have no tile on the overview, so they were
 * the only boards that still opened on a skeleton every session.
 *
 * This closes that gap by fetching those two entry queries once, after the
 * shell has painted. Both are cheap and neither touches the upstream vendor:
 * the Network route is served from the backend's TTL-cached Cato snapshot, and
 * the Security route from stored rollups — a prefetch costs Cato's 15-calls-a-
 * minute limit and TAP's 1800-per-24h budget exactly nothing.
 *
 * Deliberately not a general "prefetch everything" hook: prefetching a route
 * that does fan out upstream would spend someone else's rate limit to save a
 * skeleton, which is the wrong trade.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { networkOverviewQuery } from "@/lib/api/hooks/network";
import { securityOverviewQuery } from "@/lib/api/hooks/security";
import { logger } from "@/lib/logger";

/**
 * Prefetch the Network and Security entry queries once, on idle.
 *
 * Runs from the authenticated shell, so it never fires for a signed-out
 * visitor. `prefetchQuery` respects `staleTime`, so a board already visited
 * this session is a no-op rather than a second request.
 */
export function usePrefetchColdBoards() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Yield to first paint. Landing on /overview fires seven requests of its
    // own; queueing behind them keeps the page the user is looking at fast.
    const schedule =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback
        : (callback: () => void) => setTimeout(callback, 1_000);

    let cancelled = false;

    const handle = schedule(() => {
      if (cancelled) {
        return;
      }
      void Promise.all([
        queryClient.prefetchQuery(networkOverviewQuery()),
        queryClient.prefetchQuery(securityOverviewQuery()),
      ]).then(() => {
        logger.debug("[prefetch] network + security boards warmed");
      });
    });

    return () => {
      cancelled = true;
      if (typeof cancelIdleCallback === "function" && typeof handle === "number") {
        cancelIdleCallback(handle);
      }
    };
  }, [queryClient]);
}
